import { compact, mapValues, omit, pickBy, uniq } from "lodash";
import { useCallback, useMemo, useRef } from "react";

// A deleted item: its id when it has one, and its key on screen. An item a
// save in flight is creating has no id yet: that save's response gives it one.
export type DeletedItem = { id?: number | null; uuid?: string };

// Saved items the user deleted, by list ("roles", a role's criteria, a
// competency's criteria...). A save deletes only the ids it names: a list
// that leaves an item out keeps it. So each item waits here until a save that
// sent its id succeeds, and a failed save's retry sends it again.
//
// It also keeps the ids saves gave the items they created, by the key each
// was sent with (see withSavedId): what turns a deleted item's key into an
// id, and what a save built from an older render sends as an item's id.
const usePendingDeletes = () => {
  const pendingRef = useRef<Record<string, ReadonlyArray<DeletedItem>>>({});
  // Filled from every response's createdIds, before anything else runs, so an
  // item deleted or edited while its save ran still gets its id.
  const idByUuidRef = useRef(new Map<string, number>());

  const idOf = useCallback(
    (item: DeletedItem) =>
      item.id || (item.uuid ? idByUuidRef.current.get(item.uuid) : undefined),
    [],
  );

  const add = useCallback((list: string, items: ReadonlyArray<DeletedItem>) => {
    const current = pendingRef.current[list] ?? [];
    const added = items.filter(
      (item) =>
        (item.id || item.uuid) &&
        !current.some(
          (pending) =>
            (!!item.id && pending.id === item.id) ||
            (!!item.uuid && pending.uuid === item.uuid),
        ),
    );
    if (added.length > 0) {
      pendingRef.current = {
        ...pendingRef.current,
        [list]: [...current, ...added],
      };
    }
  }, []);

  // From a save's response: the id each item it created was given.
  const learnCreatedIds = useCallback(
    (createdIds?: ReadonlyArray<{ clientId: string; id: number }> | null) => {
      (createdIds ?? []).forEach(({ clientId, id }) =>
        idByUuidRef.current.set(clientId, id),
      );
    },
    [],
  );

  // Read when a save is built, never kept: it grows with every save.
  const savedIds = useCallback(
    (): ReadonlyMap<string, number> => idByUuidRef.current,
    [],
  );

  // The ids each list can send now, read once when a save starts. An item
  // still waiting on its id stays for a later save.
  const snapshot = useCallback(
    (): Record<string, ReadonlyArray<number>> =>
      pickBy(
        mapValues(pendingRef.current, (items) =>
          uniq(compact(items.map(idOf))),
        ),
        (ids) => ids.length > 0,
      ),
    [idOf],
  );

  const get = useCallback(
    (list: string): ReadonlyArray<number> => snapshot()[list] ?? [],
    [snapshot],
  );

  // After a save that sent them succeeds, or when an item is put back (a
  // holder added again). Only these: more may have been deleted meanwhile.
  const remove = useCallback(
    (list: string, ids: ReadonlyArray<number>) => {
      const left = (pendingRef.current[list] ?? []).filter((item) => {
        const id = idOf(item);
        return !id || !ids.includes(id);
      });
      pendingRef.current =
        left.length > 0
          ? { ...pendingRef.current, [list]: left }
          : omit(pendingRef.current, list);
    },
    [idOf],
  );

  // Lists that went away as a whole (their role deleted, say), or were saved
  // elsewhere: nothing in them is sent any more.
  const clear = useCallback((isCleared: (list: string) => boolean) => {
    pendingRef.current = pickBy(
      pendingRef.current,
      (_items, list) => !isCleared(list),
    );
  }, []);

  return useMemo(
    () => ({
      add,
      learnCreatedIds,
      savedIds,
      // An item's id: its own, or the one a save gave it since.
      savedId: idOf,
      snapshot,
      get,
      remove,
      clear,
    }),
    [add, learnCreatedIds, savedIds, idOf, snapshot, get, remove, clear],
  );
};

export default usePendingDeletes;
